<!--
  One run. The header is the toggle; the entries live in the panel it controls.

  Layout: the header stacks on a phone (the period, the timestamp and the state chip do not fit on one 320px
  line) and sits on one row from `sm` up. Nothing here is width-conditional in JS — it reflows.
-->
<article class="rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-200">
  <!-- The whole header is the button, so the tap target is the row rather than a chevron. -->
  <button type="button"
          class="flex w-full flex-col gap-2 rounded-2xl px-4 py-3 text-left transition-colors duration-200
                 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                 focus-visible:outline-theme-primary-500 sm:flex-row sm:items-center sm:justify-between"
          [attr.aria-expanded]="expanded()" [attr.aria-controls]="panelId()"
          (click)="toggled.emit(run().id)">
    <span class="min-w-0">
      <span class="block truncate text-sm font-bold tracking-tight text-gray-900">{{ periodLabel() }}</span>
      <span class="block text-xs text-gray-500">
        Angefordert am {{ run().createdUtc | date: 'dd.MM.yyyy, HH:mm' }} Uhr
        @if (containerLabel(); as container) {
          · {{ container }}
        }
        @if (run().itemCount > 1) {
          · {{ run().completedCount }} von {{ run().itemCount }} Kassen fertig
        }
      </span>
    </span>

    <span class="flex shrink-0 items-center gap-2">
      @if (running()) {
        <!-- A quiet, always-moving hint that something is happening. The state chip beside it says WHAT. -->
        <span class="h-2 w-2 animate-pulse rounded-full bg-theme-primary-500" aria-hidden="true"></span>
      }
      <span class="rounded-full px-2.5 py-1 text-xs font-semibold"
            [class.bg-green-50]="run().state === 'completed'"
            [class.text-green-700]="run().state === 'completed'"
            [class.bg-theme-primary-50]="running()"
            [class.text-gray-600]="running()"
            [class.bg-amber-50]="run().state === 'completedWithErrors'"
            [class.text-amber-700]="run().state === 'completedWithErrors'"
            [class.bg-red-50]="run().state === 'failed'"
            [class.text-red-700]="run().state === 'failed'">{{ stateLabel() }}</span>
    </span>
  </button>

  @if (expanded()) {
    <div [id]="panelId()" class="border-t border-gray-100 px-4 py-3">
      @if (runError(); as message) {
        <p class="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{{ message }}</p>
      }

      @if (items().length === 0 && !runError()) {
        <p class="text-sm text-gray-500">Die Einträge dieses Exports werden geladen …</p>
      }

      <ul class="flex flex-col gap-2">
        @for (item of items(); track item.id) {
          <li class="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50/50 px-3 py-2.5
                     sm:flex-row sm:items-center sm:justify-between">
            <span class="min-w-0">
              <span class="flex flex-wrap items-center gap-2">
                <span class="truncate text-sm font-semibold text-gray-900">{{ label(item) }}</span>
                <span class="rounded-full px-2 py-0.5 text-xs font-semibold"
                      [class.bg-green-50]="tone(item) === 'success'"
                      [class.text-green-700]="tone(item) === 'success'"
                      [class.bg-gray-100]="tone(item) === 'running'"
                      [class.text-gray-600]="tone(item) === 'running'"
                      [class.bg-amber-50]="tone(item) === 'warning'"
                      [class.text-amber-700]="tone(item) === 'warning'"
                      [class.bg-red-50]="tone(item) === 'danger'"
                      [class.text-red-700]="tone(item) === 'danger'">{{ itemLabel(item) }}</span>
              </span>
              @if (reason(item); as message) {
                <span class="mt-1 block text-xs text-gray-600">{{ message }}</span>
              }
            </span>

            <span class="flex shrink-0 flex-wrap items-center gap-2">
              @if (downloadable(item)) {
                <button type="button"
                        class="inline-flex items-center justify-center gap-1.5 rounded-2xl bg-theme-primary-600
                               px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                               transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                               active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                               disabled:grayscale"
                        [disabled]="downloadingItemId() === item.id"
                        (click)="download.emit(item)">
                  <svg class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2
                             2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0
                             0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                  </svg>
                  {{ downloadingItemId() === item.id ? 'Wird geladen …' : 'Herunterladen' }}
                </button>
              }

              <!-- The server's own flag, rendered as it arrives: the rule lives in one place (#770 D3). -->
              @if (item.retryable) {
                <button type="button"
                        class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                               py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                               hover:border-gray-300 hover:bg-gray-50 active:scale-95
                               disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale"
                        [disabled]="retryingItemId() === item.id"
                        (click)="retry.emit(item)">
                  {{ retryingItemId() === item.id ? 'Wird neu erstellt …' : 'Erneut erstellen' }}
                </button>
              }
            </span>
          </li>
        }
      </ul>
    </div>
  }
</article>
